import { PublicApiAuthScope } from "@redotech/cockroachdb-codegen/tables/common/public-api-auth-scope";
import * as z from "zod";

// Access scopes for public API tokens. The generated `PublicApiAuthScope` enum
// (from the CRDB `public_api_auth_scope` enum) is the source of truth, re-exported
// here. `PUBLIC_API_SCOPE_INFO` is a `Record<PublicApiAuthScope, …>`, so a scope
// added to the DB enum won't compile until it gets an entry here too.

/** Every scope, in DB-enum declaration order. */
export const PUBLIC_API_AUTH_SCOPES: PublicApiAuthScope[] =
  Object.values(PublicApiAuthScope);

export const publicApiAuthScopeSchema = z.enum(PublicApiAuthScope);

/**
 * Prefix on every generated public API token. Vendor-namespaced so secret
 * scanners can detect leaks, and signals that the token is a secret credential.
 * The prefix is part of the token string that gets SHA-256 hashed, so changing
 * it would invalidate all previously-issued tokens.
 */
export const PUBLIC_API_TOKEN_PREFIX = "redo_sk_";

export type PublicApiScopeAccess = "read" | "write";

export interface PublicApiScopeInfo {
  scope: PublicApiAuthScope;
  /** Grouping key shared by a resource's read/write scopes, e.g. "orders". */
  resource: string;
  /** Human-readable resource name for the scope picker, e.g. "Orders". */
  resourceLabel: string;
  access: PublicApiScopeAccess;
}

export const PUBLIC_API_SCOPE_INFO: Record<
  PublicApiAuthScope,
  PublicApiScopeInfo
> = {
  [PublicApiAuthScope.OrdersRead]: {
    scope: PublicApiAuthScope.OrdersRead,
    resource: "orders",
    resourceLabel: "Orders",
    access: "read",
  },
  [PublicApiAuthScope.OrdersWrite]: {
    scope: PublicApiAuthScope.OrdersWrite,
    resource: "orders",
    resourceLabel: "Orders",
    access: "write",
  },
  [PublicApiAuthScope.CustomersRead]: {
    scope: PublicApiAuthScope.CustomersRead,
    resource: "customers",
    resourceLabel: "Customers",
    access: "read",
  },
  [PublicApiAuthScope.CustomersWrite]: {
    scope: PublicApiAuthScope.CustomersWrite,
    resource: "customers",
    resourceLabel: "Customers",
    access: "write",
  },
  [PublicApiAuthScope.ProductsRead]: {
    scope: PublicApiAuthScope.ProductsRead,
    resource: "products",
    resourceLabel: "Products",
    access: "read",
  },
  [PublicApiAuthScope.ProductsWrite]: {
    scope: PublicApiAuthScope.ProductsWrite,
    resource: "products",
    resourceLabel: "Products",
    access: "write",
  },
  [PublicApiAuthScope.ReturnsRead]: {
    scope: PublicApiAuthScope.ReturnsRead,
    resource: "returns",
    resourceLabel: "Returns",
    access: "read",
  },
  [PublicApiAuthScope.ReturnsWrite]: {
    scope: PublicApiAuthScope.ReturnsWrite,
    resource: "returns",
    resourceLabel: "Returns",
    access: "write",
  },
  [PublicApiAuthScope.WebhooksRead]: {
    scope: PublicApiAuthScope.WebhooksRead,
    resource: "webhooks",
    resourceLabel: "Webhooks",
    access: "read",
  },
  [PublicApiAuthScope.WebhooksWrite]: {
    scope: PublicApiAuthScope.WebhooksWrite,
    resource: "webhooks",
    resourceLabel: "Webhooks",
    access: "write",
  },
  [PublicApiAuthScope.InventoryRead]: {
    scope: PublicApiAuthScope.InventoryRead,
    resource: "inventory",
    resourceLabel: "Inventory",
    access: "read",
  },
  [PublicApiAuthScope.InventoryWrite]: {
    scope: PublicApiAuthScope.InventoryWrite,
    resource: "inventory",
    resourceLabel: "Inventory",
    access: "write",
  },
  [PublicApiAuthScope.InboundShipmentsRead]: {
    scope: PublicApiAuthScope.InboundShipmentsRead,
    resource: "inbound_shipments",
    resourceLabel: "Inbound shipments",
    access: "read",
  },
  [PublicApiAuthScope.InvoicesRead]: {
    scope: PublicApiAuthScope.InvoicesRead,
    resource: "invoices",
    resourceLabel: "Invoices",
    access: "read",
  },
  [PublicApiAuthScope.CustomerEventsWrite]: {
    scope: PublicApiAuthScope.CustomerEventsWrite,
    resource: "customer_events",
    resourceLabel: "Customer events",
    access: "write",
  },
  [PublicApiAuthScope.StorefrontEventsWrite]: {
    scope: PublicApiAuthScope.StorefrontEventsWrite,
    resource: "storefront_events",
    resourceLabel: "Storefront events",
    access: "write",
  },
  [PublicApiAuthScope.LocationsRead]: {
    scope: PublicApiAuthScope.LocationsRead,
    resource: "locations",
    resourceLabel: "Locations",
    access: "read",
  },
  [PublicApiAuthScope.LocationsWrite]: {
    scope: PublicApiAuthScope.LocationsWrite,
    resource: "locations",
    resourceLabel: "Locations",
    access: "write",
  },
  [PublicApiAuthScope.ShippingRead]: {
    scope: PublicApiAuthScope.ShippingRead,
    resource: "shipping",
    resourceLabel: "Shipping",
    access: "read",
  },
  [PublicApiAuthScope.ShippingWrite]: {
    scope: PublicApiAuthScope.ShippingWrite,
    resource: "shipping",
    resourceLabel: "Shipping",
    access: "write",
  },
  [PublicApiAuthScope.CampaignsRead]: {
    scope: PublicApiAuthScope.CampaignsRead,
    resource: "campaigns",
    resourceLabel: "Campaigns",
    access: "read",
  },
  [PublicApiAuthScope.CampaignsWrite]: {
    scope: PublicApiAuthScope.CampaignsWrite,
    resource: "campaigns",
    resourceLabel: "Campaigns",
    access: "write",
  },
  [PublicApiAuthScope.MarketingAutomationsRead]: {
    scope: PublicApiAuthScope.MarketingAutomationsRead,
    resource: "marketing_automations",
    resourceLabel: "Marketing automations",
    access: "read",
  },
  [PublicApiAuthScope.MarketingAutomationsWrite]: {
    scope: PublicApiAuthScope.MarketingAutomationsWrite,
    resource: "marketing_automations",
    resourceLabel: "Marketing automations",
    access: "write",
  },
  [PublicApiAuthScope.SegmentsRead]: {
    scope: PublicApiAuthScope.SegmentsRead,
    resource: "segments",
    resourceLabel: "Segments",
    access: "read",
  },
  [PublicApiAuthScope.SegmentsWrite]: {
    scope: PublicApiAuthScope.SegmentsWrite,
    resource: "segments",
    resourceLabel: "Segments",
    access: "write",
  },
  [PublicApiAuthScope.CampaignsAnalyticsRead]: {
    scope: PublicApiAuthScope.CampaignsAnalyticsRead,
    resource: "campaigns_analytics",
    resourceLabel: "Campaign analytics",
    access: "read",
  },
  [PublicApiAuthScope.MarketingAutomationsAnalyticsRead]: {
    scope: PublicApiAuthScope.MarketingAutomationsAnalyticsRead,
    resource: "marketing_automations_analytics",
    resourceLabel: "Marketing automation analytics",
    access: "read",
  },

  [PublicApiAuthScope.MarketingTemplatesRead]: {
    scope: PublicApiAuthScope.MarketingTemplatesRead,
    resource: "marketing_templates",
    resourceLabel: "Marketing templates",
    access: "read",
  },
  [PublicApiAuthScope.MarketingTemplatesWrite]: {
    scope: PublicApiAuthScope.MarketingTemplatesWrite,
    resource: "marketing_templates",
    resourceLabel: "Marketing templates",
    access: "write",
  },
  [PublicApiAuthScope.ConsolidationsRead]: {
    scope: PublicApiAuthScope.ConsolidationsRead,
    resource: "consolidations",
    resourceLabel: "Consolidations",
    access: "read",
  },
  [PublicApiAuthScope.CommerceAgentKnowledgeRead]: {
    scope: PublicApiAuthScope.CommerceAgentKnowledgeRead,
    resource: "commerce_agent_knowledge",
    resourceLabel: "Commerce Agent knowledge",
    access: "read",
  },
  [PublicApiAuthScope.CommerceAgentKnowledgeWrite]: {
    scope: PublicApiAuthScope.CommerceAgentKnowledgeWrite,
    resource: "commerce_agent_knowledge",
    resourceLabel: "Commerce Agent knowledge",
    access: "write",
  },
  [PublicApiAuthScope.CheckoutRead]: {
    scope: PublicApiAuthScope.CheckoutRead,
    resource: "checkout",
    resourceLabel: "Checkout",
    access: "read",
  },
  [PublicApiAuthScope.CheckoutWrite]: {
    scope: PublicApiAuthScope.CheckoutWrite,
    resource: "checkout",
    resourceLabel: "Checkout",
    access: "write",
  },
};

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { PublicApiAuthScope } from "@redotech/cockroachdb-codegen/tables/common/public-api-auth-scope";
